import { useRef, useEffect, useMemo } from "react";
import { useNotes } from "@gbougakov/brief-ts/react";
import type { Note } from "@gbougakov/brief-ts";
import { DateHeader } from "./DateHeader";
import { NoteCard } from "./NoteCard";
import { NoteInput } from "./NoteInput";
import { ConnectionStatus } from "../layout/ConnectionStatus";

interface DateGroup {
  key: string;
  date: Date;
  notes: Note[];
}

function groupNotesByDate(notes: Note[]): DateGroup[] {
  const groups = new Map<string, DateGroup>();

  // Sort oldest-first so the stream reads top-to-bottom chronologically
  const sorted = [...notes].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  for (const note of sorted) {
    const d = new Date(note.createdAt);
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        date: new Date(d.getFullYear(), d.getMonth(), d.getDate()),
        notes: [],
      });
    }
    groups.get(key)!.notes.push(note);
  }

  return Array.from(groups.values());
}

export function NoteStream() {
  const notes = useNotes();
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevNoteCount = useRef(notes.length);

  const dateGroups = useMemo(() => groupNotesByDate(notes), [notes]);

  // Auto-scroll to bottom on new notes
  useEffect(() => {
    if (notes.length > prevNoteCount.current) {
      scrollRef.current?.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
    prevNoteCount.current = notes.length;
  }, [notes.length]);

  // Scroll to bottom on initial load
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [dateGroups.length > 0]);

  return (
    <div className="flex flex-col h-[100dvh] max-w-2xl mx-auto">
      <ConnectionStatus />

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 pb-4"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="flex flex-col gap-3 pt-16">
          {dateGroups.map((group) => (
            <div key={group.key} className="flex flex-col gap-3">
              <DateHeader date={group.date} />
              {group.notes.map((note) => (
                <NoteCard key={note.id} note={note} />
              ))}
            </div>
          ))}
        </div>
      </div>

      <NoteInput />
    </div>
  );
}
